import { useCallback, useMemo, useState } from "react";
import {
  IInOfficeEvent,
  IFormAssignmentForm,
} from "../../../../types/formAssignments/IInOfficeEvent";
import { IFormData } from "../../../../types/formsTable/FormTable.interface";
import { IFormPackage } from "../../../../types/formAssignments/packages/IFormPackage";
import { EFormStatus } from "src/enums/EFormStatus";
import { EActiveStatus } from "../../../../types/formsTable";
import { InOfficeEventsPerPatient } from "../useInitAllInOfficeEvents";

export type PendingFormDataToAssignPerPatient = Record<
  string,
  {
    inOfficeEvent: IInOfficeEvent;
    incompleteForms: IFormAssignmentForm[];
  }
>;

const extractPendingDataFromEvent = (inOfficeEvent: IInOfficeEvent) => {
  const incompleteForms =
    inOfficeEvent.formAssignment?.forms?.filter(
      (form) =>
        form.status === EFormStatus.Todo ||
        form.status === EFormStatus.InProgress
    ) ?? [];

  return {
    inOfficeEvent,
    incompleteForms,
  };
};

function usePendingFormDataToAssign(initialInOfficeEvent: IInOfficeEvent) {
  const [pendingFormDataToAssign, setPendingFormDataToAssign] =
    useState<PendingFormDataToAssignPerPatient>({
      [initialInOfficeEvent.patient.id]:
        extractPendingDataFromEvent(initialInOfficeEvent),
    });

  // Be careful adding any dependencies to this useCallback, as
  // it will trigger the useEffect in useInitAllInOfficeEvents.
  const initDataFromAllInOfficeEvents = useCallback(
    (allInOfficeEvents: InOfficeEventsPerPatient): void => {
      const getDataToSet = (
        prevPendingFormDataToAssign: PendingFormDataToAssignPerPatient
      ) =>
        Object.fromEntries(
          Object.entries(allInOfficeEvents)
            .filter(([, events]) => events.length > 0)
            .map(([patientId, events]) => {
              if (patientId in prevPendingFormDataToAssign) {
                return [patientId, prevPendingFormDataToAssign[patientId]];
              }

              // By default, the earliest event is used, and since events
              // should already be sorted, the first one is the earliest.
              const earliestEvent = events[0];

              return [patientId, extractPendingDataFromEvent(earliestEvent)];
            })
        );

      setPendingFormDataToAssign(getDataToSet);
    },
    []
  );

  const handleFormSelection = (patientId: string, form: IFormData): void => {
    const formToAdd: IFormAssignmentForm = {
      id: form.id,
      name: form.name,
      status: EFormStatus.Todo,
      // Carried so a newly-selected PDF form still renders with the PDF badge.
      sourceType: form.sourceType,
    };

    setPendingFormDataToAssign((prev) => {
      const currentEvent = prev[patientId];
      if (!currentEvent) {
        throw new Error(`No event found for patient ID: ${patientId}`);
      }

      return {
        ...prev,
        [patientId]: {
          ...currentEvent,
          incompleteForms: [...currentEvent.incompleteForms, formToAdd],
        },
      };
    });
  };

  const handleDeleteForm = (patientId: string, formId: number): void => {
    setPendingFormDataToAssign((prev) => {
      const currentEvent = prev[patientId];
      if (!currentEvent) {
        throw new Error(`No event found for patient ID: ${patientId}`);
      }

      return {
        ...prev,
        [patientId]: {
          ...currentEvent,
          incompleteForms: currentEvent.incompleteForms.filter(
            (form) => form.id !== formId
          ),
        },
      };
    });
  };

  const handlePackageSelection = (
    patientId: string,
    formPackage: IFormPackage
  ): void => {
    const formsToAdd: IFormAssignmentForm[] = formPackage.forms
      .filter((form) => form.activeStatus === EActiveStatus.Active)
      .map((form) => ({
        id: form.formId,
        name: form.name,
        status: EFormStatus.Todo,
        packageName: formPackage.name,
        packageId: formPackage.id,
      }));

    setPendingFormDataToAssign((prev) => {
      const currentEvent = prev[patientId];
      if (!currentEvent) {
        throw new Error(`No event found for patient ID: ${patientId}`);
      }

      return {
        ...prev,
        [patientId]: {
          ...currentEvent,
          incompleteForms: [...currentEvent.incompleteForms, ...formsToAdd],
        },
      };
    });
  };

  const handleInOfficeEventChange = (inOfficeEvent: IInOfficeEvent): void => {
    const patientId = inOfficeEvent.patient.id;

    setPendingFormDataToAssign((prev) => ({
      ...prev,
      [patientId]: extractPendingDataFromEvent(inOfficeEvent),
    }));
  };

  const totalIncompleteFormsCount = useMemo(
    () =>
      Object.values(pendingFormDataToAssign).reduce(
        (acc, { incompleteForms }) => acc + incompleteForms.length,
        0
      ),
    [pendingFormDataToAssign]
  );

  return {
    pendingFormDataToAssign,
    handleFormSelection,
    handleDeleteForm,
    handlePackageSelection,
    initDataFromAllInOfficeEvents,
    handleInOfficeEventChange,
    totalIncompleteFormsCount,
  };
}

export default usePendingFormDataToAssign;
